import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  IT_SUPPORT_TYPE, isHospitalProduct, itSupportDescription, parseItSupportDescription,
  priceInCurrency, serviceMonthOptions,
} from "@/lib/itSupport";

const MONTH_OPTIONS = serviceMonthOptions().map((m) => ({ value: m, label: m }));

const fmt = (n) => new Intl.NumberFormat("en-TZ").format(Math.round(n || 0));
// USD keeps cents; TZS is shown in whole shillings.
const fmtIn = (currency, n) => currency === "USD"
  ? new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0)
  : fmt(n);
// `base` (never saved) remembers a catalog item's own price and currency, so
// the line can be re-converted when the invoice's currency or rate changes.
// It's cleared the moment someone types a price by hand.
const emptyRow = () => ({ product: "", description: "", quantity: 1, unit_price: 0, custom: false, base: null });

// Shared line-item editor used by Invoices (products + VAT) and Sales
// (products OR link to an existing unpaid invoice, plus recurring-billing
// detection). Reports the computed {items, amount, tax, ...} back to the
// parent form via onChange whenever anything changes.
//
// allowCurrency (Invoices): the invoice can be billed in USD at a rate fixed
// on the invoice. Item prices and amount_fc/tax_fc are then in USD, while
// amount/tax stay in TZS (USD x rate) -- the backend recomputes those, see
// backend/routes/invoice_currency.py. Locked once a payment exists.
export function LineItemsEditor({ value, onChange, allowInvoiceLink = false, allowVat = false, allowCurrency = false }) {
  const [products, setProducts] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [source, setSource] = useState(value?.linked_invoice ? "invoice" : "products");
  // IT Support invoices: every line is one hospital for one month.
  const hospitalMode = value?.invoice_type === IT_SUPPORT_TYPE;
  const [rows, setRows] = useState(() => {
    const items = value?.items?.length ? value.items : [emptyRow()];
    // Lines saved before hospital mode existed: recover hospital + month from the wording.
    return hospitalMode
      ? items.map((r) => (r.hospital ? r : { ...r, ...(parseItSupportDescription(r.description) || {}) }))
      : items;
  });
  const [vatEnabled, setVatEnabled] = useState(!!value?.vat_enabled);
  const [linkedInvoice, setLinkedInvoice] = useState(value?.linked_invoice || "");
  const [currency, setCurrency] = useState(allowCurrency && value?.currency === "USD" ? "USD" : "TZS");
  const [rate, setRate] = useState(value?.exchange_rate ? String(value.exchange_rate) : "");
  const currencyLocked = allowCurrency && (value?.amount_paid || 0) > 0;

  useEffect(() => {
    api.get("/products/", { params: { limit: 500 } }).then(({ data }) => setProducts(data.items || [])).catch(() => {});
    if (allowInvoiceLink) {
      api.get("/invoices/", { params: { limit: 500 } })
        .then(({ data }) => setUnpaidInvoices((data.items || []).filter((i) => i.status !== "paid")))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productOptions = products.map((p) => ({ value: p.sku, label: `${p.name} (${p.sku})` }));
  // Once a customer is chosen on the Sale, only that customer's own unpaid
  // invoices are worth linking to -- showing every customer's invoices here
  // just invites picking the wrong one.
  const customerInvoices = value?.customer
    ? unpaidInvoices.filter((i) => i.customer === value.customer)
    : [];
  const invoiceOptions = customerInvoices.map((i) => ({
    value: i.id, label: `${i.invoice_no} — TZS ${fmt(i.amount)} (${i.status})`,
  }));
  const invoicePickerPlaceholder = !value?.customer
    ? "Select a customer first"
    : invoiceOptions.length
      ? "Select an unpaid invoice…"
      : "No unpaid invoices for this customer";

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setRows(next);
    emit(next, vatEnabled, "products", "");
  };
  const chooseCurrency = (cur) => {
    if (cur === currency) return;
    setCurrency(cur);
    const next = repriced(rows, cur, rate);
    setRows(next);
    emit(next, vatEnabled, source, linkedInvoice, cur, rate);
    if (rows.some((r) => !r.base && Number(r.unit_price) > 0)) {
      toast.warning(`Prices typed by hand weren't converted. Check they're in ${cur}.`);
    }
    // Start from the rate on the latest USD invoice; staff confirm it's today's.
    if (cur === "USD" && !rate) {
      api.get("/invoices/last-exchange-rate").then(({ data }) => {
        if (data?.exchange_rate) {
          const r = String(data.exchange_rate);
          setRate(r);
          const next = repriced(rows, cur, r);
          setRows(next);
          emit(next, vatEnabled, source, linkedInvoice, cur, r);
        }
      }).catch(() => {});
    }
  };
  const changeRate = (r) => {
    setRate(r);
    const next = repriced(rows, currency, r);
    setRows(next);
    emit(next, vatEnabled, source, linkedInvoice, currency, r);
  };
  // The currency line prices are in: the invoice's own, or TZS (Sales).
  const lineCurrency = (cur = currency) => (allowCurrency ? cur : "TZS");

  const pickProduct = (idx, sku) => {
    const p = products.find((p) => p.sku === sku);
    if (!p) return;
    const base = { price: Number(p.price) || 0, currency: p.currency === "USD" ? "USD" : "TZS" };
    const converted = priceInCurrency(base.price, base.currency, lineCurrency(), rate);
    if (converted === null) {
      toast.warning(lineCurrency() === "USD"
        ? `${p.name} is priced in TZS. Enter the exchange rate and its USD price fills in.`
        : `${p.name} is priced in USD. Switch this invoice to USD, or type the TZS price.`);
    }
    updateRow(idx, { product: sku, description: p.name, unit_price: converted ?? 0, base });
  };
  const toggleCustom = (idx, custom) => updateRow(idx, { custom, product: "", description: "", unit_price: 0, base: null });

  // Re-prices catalog lines for a new currency/rate; hand-typed prices stay.
  const repriced = (currentRows, cur, rt) => currentRows.map((r) => (r.base
    ? { ...r, unit_price: priceInCurrency(r.base.price, r.base.currency, lineCurrency(cur), rt) ?? 0 }
    : r));

  // ---- IT Support: hospital lines ----
  // SKU order (IT-0001, IT-0002, ...) = the order hospitals were added.
  const hospitals = products.filter(isHospitalProduct)
    .sort((a, b) => String(a.sku || "").localeCompare(String(b.sku || "")));
  const hospitalOptions = hospitals.map((h) => ({ value: h.name, label: h.name }));

  // One complete line for `hospital` in `month`, priced from the catalog.
  const hospitalLine = (hospital, month, warn = true) => {
    const p = hospitals.find((h) => h.name === hospital);
    const base = p ? { price: Number(p.price) || 0, currency: p.currency === "USD" ? "USD" : "TZS" } : null;
    const converted = base ? priceInCurrency(base.price, base.currency, lineCurrency(), rate) : null;
    if (warn && base && converted === null) {
      toast.warning(lineCurrency() === "USD"
        ? `${hospital} is priced in TZS. Enter the exchange rate and its USD price fills in.`
        : `${hospital} is priced in USD. Switch this invoice to USD, or type the TZS price.`);
    }
    return {
      ...emptyRow(), product: p?.sku || "", hospital, service_month: month || "",
      description: itSupportDescription(hospital, month), unit_price: converted ?? 0, base,
    };
  };
  // Lines recovered from an older invoice's wording exist only in this
  // editor until reported -- hand them to the form once, so saving without
  // touching them still sends hospital + month.
  useEffect(() => {
    if (hospitalMode && rows.some((r, i) => r.hospital && !value?.items?.[i]?.hospital)) {
      emit(rows, vatEnabled, source, linkedInvoice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pickHospital = (idx, hospital) => {
    const line = hospitalLine(hospital, rows[idx]?.service_month);
    updateRow(idx, { product: line.product, hospital, description: line.description, unit_price: line.unit_price, base: line.base });
  };
  const pickMonth = (idx, month) =>
    updateRow(idx, { service_month: month, description: itSupportDescription(rows[idx]?.hospital, month) });
  const lastMonth = () => [...rows].reverse().find((r) => r.service_month)?.service_month || "";
  const addHospitalRow = () => {
    const next = [...rows, { ...emptyRow(), service_month: lastMonth(), description: itSupportDescription("", lastMonth()) }];
    setRows(next); emit(next, vatEnabled, source, linkedInvoice);
  };
  // Fills in every hospital not already on the invoice, for the month in use.
  const addAllHospitals = () => {
    const month = lastMonth();
    const kept = rows.filter((r) => r.hospital);
    const missing = hospitals.filter((h) => !kept.some((r) => r.hospital === h.name));
    const next = [...kept, ...missing.map((h) => hospitalLine(h.name, month, false))];
    if (missing.some((h) => priceInCurrency(h.price, h.currency, lineCurrency(), rate) === null)) {
      toast.warning("Some hospital fees are in another currency. Enter the exchange rate and they fill in.");
    }
    setRows(next.length ? next : [emptyRow()]); emit(next, vatEnabled, source, linkedInvoice);
  };
  const addRow = () => { const next = [...rows, emptyRow()]; setRows(next); emit(next, vatEnabled, source, linkedInvoice); };
  const removeRow = (idx) => { const next = rows.filter((_, i) => i !== idx); setRows(next.length ? next : [emptyRow()]); emit(next, vatEnabled, source, linkedInvoice); };

  const subtotal = rows.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unit_price) || 0), 0);

  function emit(currentRows, vat, src, invNo, cur = currency, rt = rate) {
    if (src === "invoice") {
      const inv = unpaidInvoices.find((i) => i.invoice_no === invNo || i.id === invNo);
      onChange({
        items: [], amount: inv?.amount || 0, tax: 0, vat_enabled: false,
        linked_invoice: inv?.invoice_no || "", next_due_date: null,
      });
      return;
    }
    const sub = currentRows.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.unit_price) || 0), 0);
    const tax = vat ? sub * 0.15 : 0;
    const hasRecurring = currentRows.some((r) => products.find((p) => p.sku === r.product)?.recurring === "monthly");
    const next_due_date = hasRecurring
      ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      : null;
    const totals = !allowCurrency ? { amount: sub, tax }
      : cur === "USD"
        // amount/tax here are only a preview; the backend derives the real TZS figures.
        ? { currency: "USD", exchange_rate: Number(rt) || null, amount_fc: sub, tax_fc: tax,
            amount: sub * (Number(rt) || 0), tax: tax * (Number(rt) || 0) }
        : { currency: "TZS", exchange_rate: null, amount_fc: null, tax_fc: null, amount: sub, tax };
    onChange({
      items: currentRows.map((r) => ({
        // A hospital line's wording is always rebuilt from its hospital + month.
        product: r.product, quantity: Number(r.quantity) || 0,
        description: r.hospital ? itSupportDescription(r.hospital, r.service_month) : r.description,
        unit_price: Number(r.unit_price) || 0, total: (Number(r.quantity) || 0) * (Number(r.unit_price) || 0),
        // Hospital lines also keep their parts, so the line can be edited later.
        ...(r.hospital ? { hospital: r.hospital, service_month: r.service_month || "" } : {}),
      })),
      ...totals, vat_enabled: vat, linked_invoice: "", next_due_date,
    });
  }

  const chooseSource = (src) => { setSource(src); emit(rows, vatEnabled, src, linkedInvoice); };
  const chooseInvoice = (invNo) => { setLinkedInvoice(invNo); emit(rows, vatEnabled, "invoice", invNo); };
  const toggleVat = (checked) => { setVatEnabled(checked); emit(rows, checked, source, linkedInvoice); };

  return (
    <div className="md:col-span-2 space-y-3 border border-border rounded-lg p-4">
      {allowInvoiceLink && (
        <div className="flex items-center gap-2 mb-2">
          <Button type="button" size="sm" variant={source === "products" ? "default" : "outline"}
            className={source === "products" ? "kn-gradient-bg text-white border-0" : ""}
            onClick={() => chooseSource("products")} data-testid="sale-source-products">
            Products
          </Button>
          <Button type="button" size="sm" variant={source === "invoice" ? "default" : "outline"}
            className={source === "invoice" ? "kn-gradient-bg text-white border-0" : ""}
            onClick={() => chooseSource("invoice")} data-testid="sale-source-invoice">
            Existing Invoice
          </Button>
        </div>
      )}

      {source === "invoice" ? (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.16em]">Unpaid Invoice</Label>
          <SearchableSelect
            value={linkedInvoice}
            onValueChange={chooseInvoice}
            options={invoiceOptions}
            placeholder={invoicePickerPlaceholder}
            disabled={!value?.customer}
            testId="sale-invoice-picker"
          />
        </div>
      ) : (
        <>
          {allowCurrency && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 border-b border-border">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em]">Currency</Label>
                <div className="flex items-center gap-2">
                  {["TZS", "USD"].map((c) => (
                    <Button key={c} type="button" size="sm" disabled={currencyLocked}
                      variant={currency === c ? "default" : "outline"}
                      className={currency === c ? "kn-gradient-bg text-white border-0" : ""}
                      onClick={() => chooseCurrency(c)} data-testid={`invoice-currency-${c}`}>
                      {c}
                    </Button>
                  ))}
                </div>
              </div>
              {currency === "USD" && (
                <div className="space-y-1.5">
                  <Label htmlFor="exchange-rate" className="text-xs uppercase tracking-[0.16em]">
                    Exchange Rate (TZS per 1 USD)<span className="text-rose-500 ml-1">*</span>
                  </Label>
                  <MoneyInput id="exchange-rate" value={rate} disabled={currencyLocked}
                    onChange={(e) => changeRate(e.target.value)}
                    placeholder="e.g. 2,650" data-testid="invoice-exchange-rate" />
                </div>
              )}
              {currencyLocked && (
                <p className="sm:col-span-2 text-xs text-muted-foreground">
                  Currency and rate are locked because a payment has already been recorded on this invoice.
                </p>
              )}
            </div>
          )}
          {hospitalMode ? (
            <>
              <Label className="text-xs uppercase tracking-[0.16em]">
                Hospitals{currency === "USD" && " (fees in USD)"}
              </Label>
              {hospitals.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="no-hospitals">
                  No hospitals yet. Add them on Products &amp; Price List in the &quot;IT Support&quot; category.
                </p>
              )}
              {rows.map((row, idx) => (
                <div key={idx} className="p-3 rounded-lg border border-border bg-muted/20 space-y-2" data-testid={`hospital-line-${idx}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,11.5rem)_minmax(0,7.5rem)_2.25rem] gap-2 items-center [&>*]:min-w-0">
                    <SearchableSelect
                      value={row.hospital || ""}
                      onValueChange={(v) => pickHospital(idx, v)}
                      options={hospitalOptions}
                      placeholder="Hospital…"
                      testId={`hospital-select-${idx}`}
                    />
                    <SearchableSelect
                      value={row.service_month || ""}
                      onValueChange={(v) => pickMonth(idx, v)}
                      options={MONTH_OPTIONS}
                      placeholder="Month…"
                      testId={`hospital-month-${idx}`}
                    />
                    <MoneyInput value={row.unit_price}
                      onChange={(e) => updateRow(idx, { unit_price: e.target.value, base: null })}
                      placeholder="Monthly fee" data-testid={`hospital-price-${idx}`} />
                    <Button type="button" size="icon" variant="ghost" className="justify-self-end" onClick={() => removeRow(idx)} data-testid={`remove-hospital-line-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {/* Exactly what prints on the invoice -- generated, not typed. */}
                  <p className={`text-sm ${row.hospital && row.service_month ? "" : "text-muted-foreground"}`} data-testid={`hospital-description-${idx}`}>
                    {itSupportDescription(row.hospital, row.service_month)}
                  </p>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={addHospitalRow} data-testid="add-hospital-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add hospital
                </Button>
                {hospitals.length > 0 && (
                  <Button type="button" size="sm" variant="outline" onClick={addAllHospitals} data-testid="add-all-hospitals">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add all hospitals ({hospitals.length})
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
          <Label className="text-xs uppercase tracking-[0.16em]">Line Items{currency === "USD" && " (prices in USD)"}</Label>
          {rows.map((row, idx) => (
            <div key={idx} className="p-3 rounded-lg border border-border bg-muted/20 space-y-2">
              <div className="flex items-center gap-2">
                {/* min-w-0 lets a long product name truncate instead of widening the row */}
                <div className="flex-1 min-w-0">
                  {row.custom ? (
                    <Input
                      value={row.product}
                      onChange={(e) => updateRow(idx, { product: e.target.value })}
                      placeholder="Custom item name (not in the product catalog)"
                      data-testid={`line-item-custom-${idx}`}
                    />
                  ) : (
                    <SearchableSelect
                      value={row.product}
                      onValueChange={(v) => pickProduct(idx, v)}
                      options={productOptions}
                      placeholder="Select product…"
                      testId={`line-item-product-${idx}`}
                    />
                  )}
                </div>
                <Button type="button" size="sm" variant="ghost" className="shrink-0 text-xs" onClick={() => toggleCustom(idx, !row.custom)} data-testid={`toggle-custom-${idx}`}>
                  {row.custom ? "Use catalog item" : "Custom item"}
                </Button>
                <Button type="button" size="icon" variant="ghost" className="shrink-0" onClick={() => removeRow(idx)} data-testid={`remove-line-item-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Input
                value={row.description}
                onChange={(e) => updateRow(idx, { description: e.target.value })}
                placeholder="Description (optional)"
                data-testid={`line-item-description-${idx}`}
              />
              <div className="grid grid-cols-3 gap-2 items-end">
                <Input type="number" min="1" value={row.quantity}
                  onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                  placeholder="Qty" data-testid={`line-item-qty-${idx}`} />
                <MoneyInput value={row.unit_price}
                  onChange={(e) => updateRow(idx, { unit_price: e.target.value, base: null })}
                  placeholder="Unit price" data-testid={`line-item-price-${idx}`} />
                <div className="text-sm font-medium text-right pr-2">
                  {fmtIn(currency, (Number(row.quantity) || 0) * (Number(row.unit_price) || 0))}
                </div>
              </div>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="add-line-item">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add item
          </Button>
            </>
          )}

          {allowVat && (
            <div className="flex items-center gap-2 pt-2">
              <Checkbox id="vat-toggle" checked={vatEnabled} onCheckedChange={toggleVat} data-testid="vat-toggle" />
              <Label htmlFor="vat-toggle" className="text-sm font-normal cursor-pointer">Apply VAT (15%)</Label>
            </div>
          )}

          <div className="text-right text-sm space-y-0.5 pt-2 border-t border-border">
            <p>Subtotal: <span className="font-semibold">{currency} {fmtIn(currency, subtotal)}</span></p>
            {allowVat && vatEnabled && <p>VAT (15%): <span className="font-semibold">{currency} {fmtIn(currency, subtotal * 0.15)}</span></p>}
            <p className="font-display text-base">Total: <span className="font-semibold">{currency} {fmtIn(currency, subtotal * (allowVat && vatEnabled ? 1.15 : 1))}</span></p>
            {currency === "USD" && (
              <p className="text-muted-foreground" data-testid="invoice-total-tzs">
                {Number(rate) > 0
                  ? <>Total in TZS @ {fmtIn("USD", Number(rate))}: <span className="font-semibold text-foreground">TZS {fmt(subtotal * (allowVat && vatEnabled ? 1.15 : 1) * Number(rate))}</span></>
                  : "Enter the exchange rate to see the TZS total"}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
